'use client';

import { ComponentProps, forwardRef } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import { cn } from '@utils/cn';
import { useHaptics } from '@hooks/use-haptics';

type Feedback = 'scale' | 'opacity' | 'highlight';

type PressableProps = ComponentProps<typeof motion.button> & {
  feedback?: Feedback;
};

const TAP_BY_FEEDBACK: Record<
  Feedback,
  ComponentProps<typeof motion.button>['whileTap']
> = {
  scale: { scale: 0.97, opacity: 0.85 },
  opacity: { opacity: 0.6 },
  highlight: {}
};

/**
 * Touch-feedback primitive for tab items, list rows, and icon buttons.
 * `highlight` uses a background flash via CSS (active:) instead of transform,
 * for full-width rows where scaling looks wrong.
 */
export const Pressable = forwardRef<HTMLButtonElement, PressableProps>(
  function Pressable(
    { feedback = 'scale', className, onClick, ...props },
    ref
  ) {
    const haptics = useHaptics();
    const reducedMotion = useReducedMotion();

    return (
      <motion.button
        ref={ref}
        type="button"
        whileTap={reducedMotion ? undefined : TAP_BY_FEEDBACK[feedback]}
        transition={{ duration: reducedMotion ? 0 : 0.16 }}
        onClick={event => {
          haptics.selection();
          onClick?.(event);
        }}
        className={cn(
          feedback === 'highlight' && 'transition-colors active:bg-muted/70',
          className
        )}
        {...props}
      />
    );
  }
);
